import { Color } from 'three';
import _ from 'underscore';

import { t } from '../../../../../../utils/i18-next';
import { isValidNumber } from '../../../../../../utils/math/number';
import { copyMap, isDefined } from '../../../../../../utils/variables';
import { getDefaultEnumValueColor } from '../../../../../elements/design/colors';
import { DesignNode, RevertChange } from '../../../../../elements/design/design-node/design-node';
import {
  AttributeData,
  AttributeId,
  SkyMapAttribute,
  ValueId,
} from '../../../../../elements/design/skymap-extras';
import { DesignTool } from '../../../../design-tool';
import { SelectionResult } from './select';

export type ClearAttribute = {
  clearAttribute?: true;
};

export type AttributeDataWithId = (AttributeData & ClearAttribute) & {
  id: AttributeId;
};

function compareActiveAttributes(a: AttributeData | undefined, b: AttributeData & ClearAttribute) {
  if (!isDefined(a)) {
    return b.clearAttribute;
  }

  if (b.clearAttribute) {
    return false;
  }

  if (a.type === 'enum' && b.type === 'enum' && b.index === a.index) {
    return true;
  }

  if (a.type === 'string' && b.type === 'string' && a.value === b.value) {
    return true;
  }

  if (a.type === 'number' && b.type === 'number' && a.value === b.value) {
    return true;
  }

  return false;
}

export function clearAttributesFromSelection(
  tool: DesignTool,
  selection: SelectionResult,
): RevertChange[] {
  return [
    ...selection.points.map((id) =>
      tool.asset.designNode.upsertPoint({ id, attributes: new Map() }),
    ),
    ...selection.faces.map((id) => tool.asset.designNode.upsertFace({ id, attributes: new Map() })),
    ...selection.lineSegments.map((id) =>
      tool.asset.designNode.upsertLineSegment({ id, attributes: new Map() }),
    ),
  ];
}

export function getMatchingSelection(designNode: DesignNode, items: AttributeDataWithId[]) {
  function attributesMatch(attributes: Map<AttributeId, AttributeData>) {
    for (const item of items) {
      const attribute = attributes.get(item.id);
      if (!compareActiveAttributes(attribute, item)) {
        return false;
      }
    }

    return true;
  }

  function appendSelection<T>(
    id: T,
    getItem: (id: T) => { attributes: Map<AttributeId, AttributeData> },
    array: T[],
  ) {
    if (attributesMatch(getItem(id).attributes)) {
      array.push(id);
    }
  }

  const selection: SelectionResult = {
    points: [],
    faces: [],
    lineSegments: [],
  };

  for (const id of designNode.points) {
    appendSelection(id, (id) => designNode.getPointById(id), selection.points);
  }
  for (const id of designNode.lineSegments) {
    appendSelection(id, (id) => designNode.getLineSegmentById(id), selection.lineSegments);
  }
  for (const id of designNode.faces) {
    appendSelection(id, (id) => designNode.getFaceById(id), selection.faces);
  }

  return selection;
}

export function setAttributesOnSelection(
  tool: DesignTool,
  selection: SelectionResult,
  attributes: Map<AttributeId, (AttributeData & ClearAttribute) | false>,
): RevertChange[] {
  const changes: RevertChange[] = [];

  function upsertItem<T>(
    id: T,
    getItem: (id: T) => { attributes: Map<AttributeId, AttributeData> },
    upsert: (data: { id: T; attributes: Map<AttributeId, AttributeData> }) => RevertChange,
  ) {
    const newAttributes = copyMap(getItem(id).attributes, { deep: true });

    for (const [attributeId, attributeData] of attributes.entries()) {
      if (attributeData === false || attributeData.clearAttribute === true) {
        newAttributes.delete(attributeId);
      } else {
        newAttributes.set(attributeId, { ...attributeData });
      }
    }

    changes.unshift(upsert({ id, attributes: newAttributes }));
  }

  for (const id of selection.points) {
    upsertItem(
      id,
      (id) => tool.asset.designNode.getPointById(id),
      (data) => tool.asset.designNode.upsertPoint(data),
    );
  }
  for (const id of selection.faces) {
    upsertItem(
      id,
      (id) => tool.asset.designNode.getFaceById(id),
      (data) => tool.asset.designNode.upsertFace(data),
    );
  }
  for (const id of selection.lineSegments) {
    upsertItem(
      id,
      (id) => tool.asset.designNode.getLineSegmentById(id),
      (data) => tool.asset.designNode.upsertLineSegment(data),
    );
  }

  return changes;
}

/**
 * Gets the enum attribute or creates it if it does not already exist.
 */
export function getEnumAttributeOrCreate(
  tool: DesignTool,
  name: string,
): { attribute: SkyMapAttribute; change?: RevertChange } {
  const existingAttributes = tool.asset.designNode.getAttributes();

  const nextAttributeId = (
    existingAttributes.length > 0 ? _.max(existingAttributes.map((x) => x.id)) + 1 : 0
  ) as AttributeId;

  const attribute = existingAttributes.find((x) => x.name === name);
  if (isDefined(attribute)) {
    return { attribute };
  }

  const newAttribute: SkyMapAttribute = {
    id: nextAttributeId,
    type: 'enum',
    name,
    valueMap: new Map(),
    colorMap: new Map(),
    source: 'skymap-design',
  };

  return {
    attribute: newAttribute,
    change: tool.asset.designNode.addAttribute(newAttribute),
  };
}

/**
 * Gets the enum attribute or creates it if it does not already exist.
 */
export function getTextAttributeOrCreate(tool: DesignTool): {
  attribute: SkyMapAttribute;
  change?: RevertChange;
} {
  const existingAttributes = tool.asset.designNode.getAttributes();

  const nextAttributeId = (
    existingAttributes.length > 0 ? _.max(existingAttributes.map((x) => x.id)) + 1 : 0
  ) as AttributeId;

  const attribute = existingAttributes.find((x) => x.type === 'string' && x.display);

  if (isDefined(attribute)) {
    return { attribute };
  }

  const newAttribute: SkyMapAttribute = {
    id: nextAttributeId,
    type: 'string',
    display: true,
    name: t('section.tools.textButton.text', { ns: 'skyviewDesign' }),
    source: 'skymap-design',
  };

  return {
    attribute: newAttribute,
    change: tool.asset.designNode.addAttribute(newAttribute),
  };
}

/**
 * Gets the enum attribute or creates it if it does not already exist.
 */
export function getAttributeOrAdd(
  tool: DesignTool,
  newAttribute: SkyMapAttribute,
): { attribute: SkyMapAttribute; change?: RevertChange } {
  const existingAttributes = tool.asset.designNode.getAttributes();

  const nextAttributeId = (
    existingAttributes.length > 0 ? _.max(existingAttributes.map((x) => x.id)) + 1 : 0
  ) as AttributeId;

  const attribute = existingAttributes.find((x) => x.name === newAttribute.name);
  if (isDefined(attribute) && attribute.type === newAttribute.type) {
    return { attribute };
  }

  newAttribute.id = nextAttributeId;
  const result = tool.asset.designNode.addAttribute(newAttribute);

  return {
    attribute: newAttribute,
    change: result,
  };
}

/**
 * Gets the attribute data corresponding to the given attribute and it's string value.
 * If it's an enum value and the value does not exist. Then the value will be added
 * automatically to the enum attribute.
 */
export function getAttributeDataOrAddValue(
  attribute: SkyMapAttribute,
  value?: string,
  oldColor?: Color,
): { attributeData: AttributeData; change?: RevertChange } | undefined {
  if (!isDefined(value) || value === '') {
    return undefined;
  }

  if (attribute.type === 'number') {
    const number = Number(value);
    if (isValidNumber(number)) {
      return { attributeData: { type: 'number', value: number } };
    }
    return undefined;
  }

  if (attribute.type === 'string') {
    return { attributeData: { type: 'string', value } };
  }

  if (attribute.type === 'enum') {
    const enumValues = [...attribute.valueMap.entries()];
    const existingValueId = enumValues.find((x) => x[1] === value)?.[0];

    if (isDefined(existingValueId)) {
      return {
        attributeData: { type: 'enum', index: existingValueId },
      };
    }

    const nextValueId = (
      enumValues.length > 0 ? _.max(enumValues.map((x) => x[0])) + 1 : 0
    ) as ValueId;
    const nextColor = oldColor ?? getDefaultEnumValueColor(nextValueId);

    attribute.valueMap.set(nextValueId, value);
    attribute.colorMap.set(nextValueId, nextColor);

    return {
      attributeData: {
        type: 'enum',
        index: nextValueId,
      },
      change: {
        type: 'custom',
        callback: () => {
          attribute.valueMap.delete(nextValueId);
          attribute.colorMap.delete(nextValueId);
        },
      },
    };
  }

  return undefined;
}
